import { Field } from '@chakra-ui/react'
import { Select, SelectInstance } from 'chakra-react-select'
import cn from 'clsx'
import { forwardRef, useState } from 'react'

import { customScrollBarSettings } from '@/components/ui/utls'

import { IOption } from '@/types/selectOption.interface'

interface ICustomSelectBaseProps {
	fieldLabel?: string
	options: IOption[]
	rootClassname?: string
	inputClassname?: string
	placeholder?: string
	isDropdownIndicator?: string
	onBlur?: () => void
	error?: boolean
}

interface ICustomSelectMultiProps extends ICustomSelectBaseProps {
	isMulti: true
	value: IOption[]
	onChange: (value: IOption[] | null) => void
}

interface ICustomSelectSingleProps extends ICustomSelectBaseProps {
	isMulti: false
	value: IOption | null
	onChange: (value: IOption | null) => void
}

type ICustomSelectProps = ICustomSelectMultiProps | ICustomSelectSingleProps

const CustomSelect = forwardRef<SelectInstance, ICustomSelectProps>(
	(
		{
			fieldLabel,
			value,
			onChange,
			options,
			rootClassname,
			inputClassname,
			placeholder,
			isMulti,
			isDropdownIndicator = '',
			onBlur,
			error
		},
		ref
	) => {
		const [isFocused, setIsFocused] = useState(false)

		return (
			<Field.Root className={rootClassname}>
				<div className='w-full relative'>
					{fieldLabel && (
						<Field.Label className={cn('mb-2')}>{fieldLabel}</Field.Label>
					)}
					<Select
						ref={ref}
						className={cn(inputClassname)}
						isMulti={isMulti}
						placeholder={placeholder}
						isClearable={isFocused}
						options={options}
						value={value}
						onChange={onChange}
						onFocus={() => setIsFocused(true)}
						onBlur={() => {
							setIsFocused(false)
							onBlur?.()
						}}
						chakraStyles={{
							control: (base, state) => ({
								...base,
								borderWidth: '2px',
								borderColor: error
									? '#DE1135'
									: state.isFocused
									? 'black'
									: '#E8E8E8',
								borderRadius: '8px',
								backgroundColor: state.isFocused ? 'white' : '#E8E8E8',
								minH: '48px',
								w: '100%',
								fontSize: 'md'
							}),
							multiValue: base => ({
								...base,
								p: '8px',
								borderRadius: '8px',
								backgroundColor: 'gray.400',
								color: 'white'
							}),
							multiValueLabel: base => ({
								...base,
								fontSize: '16px'
							}),
							dropdownIndicator: base => ({
								...base,
								display: isDropdownIndicator
							}),
							menu: base => ({
								...base,
								borderRadius: '12px',
								overflow: 'hidden',
								width: '100%',
								boxShadow: '0px 4px 16px 0px #0000001F'
							}),
							menuList: base => ({
								...base,
								borderRadius: '12px',
								...customScrollBarSettings,
								width: '100%'
							}),
							option: (base, state) => ({
								...base,
								borderRadius: '8px',
								backgroundColor:
									state.isFocused || state.isSelected
										? '#F3F4F6'
										: 'transparent',
								color: 'black'
							})
						}}
					/>
				</div>
			</Field.Root>
		)
	}
)

export default CustomSelect

// This styles we can use for floating label. Just import floatingStyles from ../input/FloatingStyles
// and add it to Field.Label
// css={floatingStyles} style={{ background: 'none' }}
